/**
 * Keeps the layout aware of the on-screen keyboard.
 *
 * iOS does not shrink the layout viewport when the keyboard (or the AutoFill
 * "Contact" bar above it) opens — the page keeps its full height and the
 * keyboard is simply painted on top, hiding whatever was at the bottom. That is
 * how the new-category form ended up with its colour and icon rows underneath
 * the keyboard with no way to reach them (APP-07).
 *
 * `visualViewport` is the only thing that reports the truth on iOS, so we
 * measure the occluded strip ourselves and publish it as `--kb-inset` for CSS
 * to spend as extra scroll padding. Chrome Android is handled declaratively by
 * `interactive-widget=resizes-content` in the viewport meta, where this
 * measurement correctly resolves to zero.
 */

/** Below this, the difference is browser chrome settling — not a keyboard. */
const NOISE_FLOOR_PX = 24

function measure(): number {
  const vv = window.visualViewport
  if (!vv) return 0
  // Everything below the visual viewport's bottom edge is covered.
  const hidden = window.innerHeight - (vv.height + vv.offsetTop)
  return hidden > NOISE_FLOOR_PX ? Math.round(hidden) : 0
}

function publish(): void {
  document.documentElement.style.setProperty('--kb-inset', `${measure()}px`)
}

/**
 * Starts tracking. Returns a teardown so callers can stop listening.
 * Safe to call in browsers without `visualViewport`; it simply pins the inset
 * to zero and does nothing further.
 */
export function trackKeyboardInset(): () => void {
  const vv = window.visualViewport
  if (!vv) {
    document.documentElement.style.setProperty('--kb-inset', '0px')
    return () => {}
  }
  publish()
  vv.addEventListener('resize', publish)
  vv.addEventListener('scroll', publish)
  return () => {
    vv.removeEventListener('resize', publish)
    vv.removeEventListener('scroll', publish)
    document.documentElement.style.setProperty('--kb-inset', '0px')
  }
}

/**
 * Brings an element fully into view once the keyboard has finished animating.
 *
 * The delay is not decoration: calling `scrollIntoView` while the keyboard is
 * still sliding up measures against a viewport that is about to change, and the
 * element lands back under the keyboard.
 */
export function revealWhenKeyboardSettles(el: HTMLElement | null): () => void {
  if (!el) return () => {}
  const timer = window.setTimeout(() => {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, 320)
  return () => window.clearTimeout(timer)
}
